import { LGraphNode, LiteGraph } from "litegraph.js";
import { addDrivableSlider, finalizeDrivableSliders, syncDrivableSliders } from "../utils/addDrivableSlider.js";

/**
 * ScrollNode
 * ----------
 * Turns scroll wheel input into a controllable value.
 * Great for scrubbing through animations or adjusting parameters interactively.
 */
export class ScrollNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Scroll";

    this.properties = {
      active: true,
      sensitivity: 1,
      min: 0,
      max: 1,
      mode: "accumulate",
      friction: 0.95,
    };

    this.addWidget("toggle", "Active", true, (val) => {
      this.properties.active = val;
    });

    this._value = 0;
    this._velocity = 0;
    this._rawScroll = 0;

    this.addOutput("Value", "number");
    this.addOutput("Delta", "number");
    this.addOutput("Velocity", "number");

    this.addWidget("combo", "Mode", "accumulate", (val) => {
      this.properties.mode = val;
    }, { values: ["accumulate", "momentum", "spring-back"] });

    addDrivableSlider(this, "Sensitivity", "sensitivity", 1, 0.1, 5, 0.1);
    addDrivableSlider(this, "Min", "min", 0, -1000, 1000, 1);
    addDrivableSlider(this, "Max", "max", 1, -1000, 1000, 1);
    addDrivableSlider(this, "Friction", "friction", 0.95, 0.8, 0.99, 0.01);

    finalizeDrivableSliders(this, 0);

    this._lastDelta = 0;

    this._onWheel = (e) => {
      if (!this.properties.active) return;
      this._rawScroll += e.deltaY * 0.001 * this.properties.sensitivity;
      this._lastDelta = e.deltaY * 0.001 * this.properties.sensitivity;
      this._velocity += this._lastDelta;
    };
    window.addEventListener("wheel", this._onWheel, { passive: true });

    this.size = [200, 160];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  onDrawForeground(ctx) {
    const w = this.size[0];
    const h = this.size[1];
    const { min, max } = this.properties;

    // Progress bar
    const barY = h - 22;
    const barW = w - 20;
    const normalized = max !== min ? (this._value - min) / (max - min) : 0;

    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.fillRect(10, barY, barW, 6);
    ctx.fillStyle = "#4E9EFF";
    ctx.fillRect(10, barY, barW * Math.max(0, Math.min(1, normalized)), 6);

    // Value label
    ctx.fillStyle = "rgba(255,255,255,0.4)";
    ctx.font = "9px monospace";
    ctx.fillText(`val: ${this._value.toFixed(3)}`, 10, h - 6);
  }

  onExecute() {
    syncDrivableSliders(this);

    const { min, max, mode, friction } = this.properties;

    if (mode === "accumulate") {
      this._value = Math.max(min, Math.min(max, this._rawScroll));
    } else if (mode === "momentum") {
      this._velocity *= friction;
      this._value += this._velocity * (1 / 60);
      this._value = Math.max(min, Math.min(max, this._value));
    } else if (mode === "spring-back") {
      // Accumulate but spring back toward center
      this._value += this._velocity * (1 / 60);
      this._velocity *= friction;
      // Pull toward center of range
      const center = (min + max) / 2;
      this._value += (center - this._value) * 0.02;
      this._value = Math.max(min, Math.min(max, this._value));
    }

    this.setOutputData(0, this._value);
    this.setOutputData(1, this._lastDelta);
    this.setOutputData(2, this._velocity);

    this._lastDelta = 0;
    this.setDirtyCanvas(true);
  }

  onRemoved() {
    window.removeEventListener("wheel", this._onWheel);
  }
}

LiteGraph.registerNodeType("input/Scroll", ScrollNode);
